import { useState, useEffect, useCallback } from "react";

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "gfiapp-theme";

function applyThemeToDOM(mode: ThemeMode) {
  if (mode === "dark") {
    document.documentElement.classList.add("dark");
  } else if (mode === "light") {
    document.documentElement.classList.remove("dark");
  } else {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.classList.toggle("dark", prefersDark);
  }
}

function getStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch (_) { /* ignore localStorage errors in SSR/private browsing */ }
  return "dark";
}

// Apply theme immediately on load (before React renders)
applyThemeToDOM(getStoredTheme());

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeMode>(getStoredTheme);

  const setTheme = useCallback((mode: ThemeMode) => {
    setThemeState(mode);
    localStorage.setItem(STORAGE_KEY, mode);
    applyThemeToDOM(mode);
    // Dispatch event so other components using this hook re-sync
    window.dispatchEvent(new Event("gfiapp-theme-change"));
  }, []);

  useEffect(() => {
    const sync = () => setThemeState(getStoredTheme());
    window.addEventListener("gfiapp-theme-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("gfiapp-theme-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return { theme, setTheme, isDark: theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches) };
}
